import React, { useEffect, useMemo, useState } from "react";
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import Swal from "sweetalert2";
import axios from "../../utils/axiosConfig";

const STATUS_ASSOCIATED = "associated";
const STATUS_CANDIDATE = "candidate";

const ASSOCIATED_COLORS = {
    fill: "rgba(22, 163, 74, 0.16)",
    stroke: "#15803d",
    badgeBg: "#dcfce7",
    badgeColor: "#166534",
};

const CANDIDATE_COLORS = {
    fill: "rgba(248, 113, 113, 0.16)",
    stroke: "#f87171",
    badgeBg: "#fee2e2",
    badgeColor: "#b91c1c",
};

const getGeocercaId = (geocerca) =>
    String(geocerca?.ID ?? geocerca?.id ?? geocerca?.Id ?? "");

const getGeocercaName = (geocerca) =>
    geocerca?.Nombre ??
    geocerca?.nombre ??
    geocerca?.Descripcion ??
    `Geocerca ${getGeocercaId(geocerca)}`;

const getPointName = (point) =>
    point?.name ??
    point?.Nombre ??
    point?.nombre ??
    point?.Descripcion ??
    point?.description ??
    "";

const parseFirstVertex = (geocerca) => {
    const rawVertices = geocerca?.Vertices ?? geocerca?.vertices;
    const firstVertex = String(rawVertices || "").split("/").find(Boolean);
    if (!firstVertex) return null;
    const [lat, lng] = firstVertex.replace(/[()]/g, "").split(",").map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const getTrayectoId = (trayecto) =>
    trayecto?.IDTrayecto ??
    trayecto?.IDtrayecto ??
    trayecto?.IdTrayecto ??
    trayecto?.id ??
    trayecto?.ID;

const getTrayectoName = (trayecto) =>
    trayecto?.DescripcionTrayecto ??
    trayecto?.DescriptionTrayecto ??
    trayecto?.Descripcion ??
    trayecto?.Nombre ??
    `Trayecto ${getTrayectoId(trayecto)}`;

const areStringArraysEqual = (left, right) => {
    if (left === right) return true;
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((value, index) => String(value) === String(right[index]));
};

const parseTrayectoGeocercasA = (trayecto) => {
    const rawValue =
        trayecto?.GeocercasA ??
        trayecto?.geocercasA ??
        trayecto?.Geocercasa ??
        trayecto?.geocercasa ??
        "";

    return String(rawValue)
        .split(/[\s,;|]+/)
        .map((value) => value.trim())
        .filter(Boolean);
};

const openAnalysisLoader = () => {
    Swal.fire({
        title: "Analizando ruta",
        html: `
            <div style="display:grid; gap:10px; text-align:center;">
                <div id="trayecto-analysis-status" style="font-weight:700; color:#16324a;">
                    Analizando ruta seleccionada...
                </div>
                <div id="trayecto-analysis-counter" style="font-size:14px; color:#5b7083;">
                    Geocercas encontradas: 0
                </div>
            </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        didOpen: () => {
            Swal.showLoading();
        },
    });
};

const updateAnalysisLoader = (status, foundCount) => {
    const htmlContainer = Swal.getHtmlContainer();
    const statusNode = htmlContainer?.querySelector("#trayecto-analysis-status");
    const counterNode = htmlContainer?.querySelector("#trayecto-analysis-counter");

    if (statusNode) {
        statusNode.textContent = status;
    }

    if (counterNode) {
        counterNode.textContent = `Geocercas encontradas: ${foundCount}`;
    }
};

const findNearbyGeocercaIds = async (geocercas, trayecto, onProgress) => {
    const maps = window.google?.maps;
    const encodedPolyline = trayecto?.Polyline ?? trayecto?.polyline;
    if (!maps?.geometry?.encoding || !maps?.geometry?.poly || !encodedPolyline) return [];

    const toleranceMeters = 2000;
    const tolerance = toleranceMeters / 111320;

    const routeSegments = String(encodedPolyline)
        .split("||||")
        .filter(Boolean)
        .map((segment) => new maps.Polyline({
            path: maps.geometry.encoding.decodePath(segment),
        }));

    const nearbyIds = [];

    for (let index = 0; index < geocercas.length; index += 1) {
        const geocerca = geocercas[index];
        const firstVertex = parseFirstVertex(geocerca);

        if (firstVertex) {
            const point = new maps.LatLng(firstVertex.lat, firstVertex.lng);
            const isNearby = routeSegments.some((segment) =>
                maps.geometry.poly.isLocationOnEdge(point, segment, tolerance)
            );

            if (isNearby) {
                const geocercaId = getGeocercaId(geocerca);
                if (geocercaId) {
                    nearbyIds.push(geocercaId);
                }
            }
        }

        onProgress?.({
            processed: index + 1,
            total: geocercas.length,
            found: nearbyIds.length,
        });

        if ((index + 1) % 10 === 0) {
            await new Promise((resolve) => window.setTimeout(resolve, 0));
        }
    }

    return nearbyIds;
};

const getTrayectoGeocercaVisual = (isAssociated) => (
    isAssociated ? ASSOCIATED_COLORS : CANDIDATE_COLORS
);

const GeocercasTrayectoOffcanvas = ({
    isOpen,
    toggle,
    container,
    trayecto,
    geocercas: availableGeocercas = [],
    appName = "GEOCERCAS2",
    selectedIds = [],
    onSelectedIdsChange,
    onNearbyIdsDetected,
    onUpdateTrayecto,
    isUpdatingTrayecto = false,
    onApplyTrayecto,
    onClearTrayecto,
    hasUnsavedChanges = false,
    originalSelectedIds = [],
    onDiscardDraft,
    startPoint = null,
    endPoint = null,
    originalStartPoint = null,
    originalEndPoint = null,
    onStartPointChange,
    onEndPointChange,
}) => {
    const [georutas, setGeorutas] = useState([]);
    const [selectedTrayectoId, setSelectedTrayectoId] = useState("");
    const [search, setSearch] = useState("");
    const [loadingGeorutas, setLoadingGeorutas] = useState(false);
    const [applyingTrayecto, setApplyingTrayecto] = useState(false);
    const [georutasError, setGeorutasError] = useState("");
    const [nearbyIds, setNearbyIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const trayectoId = getTrayectoId(trayecto);
    const associatedBaseIds = useMemo(() => parseTrayectoGeocercasA(trayecto), [trayecto]);
    const draftAddedCount = useMemo(() => {
        const originalSet = new Set((Array.isArray(originalSelectedIds) ? originalSelectedIds : []).map(String));
        return (Array.isArray(selectedIds) ? selectedIds : []).filter((id) => !originalSet.has(String(id))).length;
    }, [originalSelectedIds, selectedIds]);
    const draftRemovedCount = useMemo(() => {
        const currentSet = new Set((Array.isArray(selectedIds) ? selectedIds : []).map(String));
        return (Array.isArray(originalSelectedIds) ? originalSelectedIds : []).filter((id) => !currentSet.has(String(id))).length;
    }, [originalSelectedIds, selectedIds]);
    const startPointId = String(startPoint?.id ?? "");
    const endPointId = String(endPoint?.id ?? "");
    const originalStartPointId = String(originalStartPoint?.id ?? "");
    const originalEndPointId = String(originalEndPoint?.id ?? "");
    const hasStartPoint = Boolean(startPointId);
    const hasEndPoint = Boolean(endPointId);
    const hasPendingPointWarning = hasStartPoint && !hasEndPoint;
    const hasUnsavedPointChanges =
        startPointId !== originalStartPointId ||
        endPointId !== originalEndPointId;

    useEffect(() => {
        if (!isOpen) return undefined;

        let cancelled = false;
        const loadGeorutas = async () => {
            setLoadingGeorutas(true);
            setGeorutasError("");

            try {
                const georutasResponse = await axios.get(
                    `${process.env.REACT_APP_SERVER}/operaciones/gettrayectos`
                );

                if (georutasResponse.data?.success === false) {
                    throw new Error(georutasResponse.data?.message || "No fue posible obtener los trayectos.");
                }

                if (cancelled) return;
                setGeorutas(
                    Array.isArray(georutasResponse.data?.data)
                        ? georutasResponse.data.data
                        : []
                );
            } catch (requestError) {
                if (cancelled) return;
                setGeorutas([]);
                setGeorutasError(
                    requestError.response?.data?.message ||
                    requestError.message ||
                    "Error al listar las georutas."
                );
            } finally {
                if (!cancelled) setLoadingGeorutas(false);
            }
        };

        loadGeorutas();
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    useEffect(() => {
        setSelectedTrayectoId(trayectoId ? String(trayectoId) : "");
    }, [trayectoId]);

    useEffect(() => {
        if (!trayectoId || !isOpen) {
            setNearbyIds([]);
            setError("");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;

        const analyzeNearbyGeocercas = async () => {
            setLoading(true);
            setError("");

            openAnalysisLoader();
            updateAnalysisLoader("Analizando ruta seleccionada...", 0);

            const detectedIds = await findNearbyGeocercaIds(
                Array.isArray(availableGeocercas) ? availableGeocercas : [],
                trayecto,
                ({ processed, total, found }) => {
                    updateAnalysisLoader(
                        `Actualizando geocercas cercanas... (${processed}/${total})`,
                        found
                    );
                }
            );

            if (cancelled) return;

            setNearbyIds((currentIds) => (
                areStringArraysEqual(currentIds, detectedIds) ? currentIds : detectedIds
            ));
            onNearbyIdsDetected?.((currentIds) => (
                areStringArraysEqual(currentIds, detectedIds) ? currentIds : detectedIds
            ));
            updateAnalysisLoader("Analisis finalizado.", detectedIds.length);
            setLoading(false);

            await new Promise((resolve) => window.setTimeout(resolve, 250));
            Swal.close();
        };

        analyzeNearbyGeocercas().catch((analysisError) => {
            if (cancelled) return;
            console.error("Error analizando geocercas del trayecto:", analysisError);
            setError("No fue posible analizar las geocercas del trayecto.");
            setLoading(false);
            Swal.close();
        });

        return () => {
            cancelled = true;
            Swal.close();
        };
    }, [availableGeocercas, isOpen, onNearbyIdsDetected, trayecto, trayectoId]);

    const normalizedGeorutas = useMemo(() => (
        georutas
            .map((georuta) => ({
                id: String(getTrayectoId(georuta) ?? ""),
                name: String(getTrayectoName(georuta)),
                raw: georuta,
            }))
            .filter((georuta) => georuta.id)
            .sort((left, right) => left.name.localeCompare(right.name))
    ), [georutas]);

    const selectedSet = useMemo(
        () => new Set((Array.isArray(selectedIds) ? selectedIds : []).map(String)),
        [selectedIds]
    );
    const associatedBaseSet = useMemo(
        () => new Set(associatedBaseIds.map(String)),
        [associatedBaseIds]
    );
    const nearbySet = useMemo(() => new Set(nearbyIds.map(String)), [nearbyIds]);

    const relatedGeocercas = useMemo(() => {
        const relatedIds = new Set([
            ...Array.from(selectedSet),
            ...Array.from(nearbySet),
            ...Array.from(associatedBaseSet),
            startPointId,
            endPointId,
        ]);

        return (Array.isArray(availableGeocercas) ? availableGeocercas : [])
            .map((geocerca) => {
                const id = getGeocercaId(geocerca);
                const isAssociated = selectedSet.has(id);
                const isNearby = nearbySet.has(id);
                const isInitiallyAssociated = associatedBaseSet.has(id);
                let status = null;

                if (isAssociated) {
                    status = STATUS_ASSOCIATED;
                } else if (isNearby || isInitiallyAssociated) {
                    status = STATUS_CANDIDATE;
                }

                return {
                    raw: geocerca,
                    id,
                    name: String(getGeocercaName(geocerca)),
                    isAssociated,
                    isNearby,
                    isInitiallyAssociated,
                    isStartPoint: startPointId === id,
                    isEndPoint: endPointId === id,
                    status,
                    colors: getTrayectoGeocercaVisual(isAssociated),
                };
            })
            .filter((geocerca) => geocerca.id && relatedIds.has(geocerca.id))
            .sort((a, b) => {
                const startDifference = Number(b.isStartPoint) - Number(a.isStartPoint);
                const endDifference = Number(b.isEndPoint) - Number(a.isEndPoint);
                const associationDifference = Number(b.isAssociated) - Number(a.isAssociated);
                const nearbyDifference = Number(b.isNearby) - Number(a.isNearby);
                return startDifference || endDifference || associationDifference || nearbyDifference || a.name.localeCompare(b.name);
            });
    }, [availableGeocercas, associatedBaseSet, endPointId, nearbySet, selectedSet, startPointId]);

    const filteredGeocercas = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return relatedGeocercas;
        return relatedGeocercas.filter((geocerca) =>
            geocerca.name.toLowerCase().includes(term) ||
            geocerca.id.toLowerCase().includes(term)
        );
    }, [relatedGeocercas, search]);

    const applyTrayectoSelection = async () => {
        if (!selectedTrayectoId || applyingTrayecto) return;
        const selectedGeoruta = normalizedGeorutas.find((georuta) => georuta.id === selectedTrayectoId);
        if (!selectedGeoruta) return;
        const currentTrayectoId = trayectoId ? String(trayectoId) : "";
        const nextTrayectoId = String(selectedGeoruta.id);

        if (hasUnsavedChanges && currentTrayectoId && currentTrayectoId !== nextTrayectoId) {
            const confirmation = await Swal.fire({
                icon: "warning",
                title: "Hay cambios sin guardar",
                text: "Si cambias de georuta ahora, perderas el borrador actual de autorizaciones. Deseas continuar?",
                showCancelButton: true,
                confirmButtonText: "Si, cambiar georuta",
                cancelButtonText: "Seguir editando",
                confirmButtonColor: "#b42318",
            });
            if (!confirmation.isConfirmed) return;
            onDiscardDraft?.();
        }

        setApplyingTrayecto(true);
        try {
            await onApplyTrayecto?.(selectedGeoruta.raw);
        } finally {
            setApplyingTrayecto(false);
        }
    };

    const toggleGeocerca = async (geocerca) => {
        const { id, name } = geocerca;

        if (selectedSet.has(id)) {
            const confirmation = await Swal.fire({
                icon: "question",
                title: "Quitar autorizacion",
                text: `Deseas quitar la autorizacion de "${name}" en el trayecto activo?`,
                showCancelButton: true,
                confirmButtonText: "Si, quitar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#b42318",
            });
            if (!confirmation.isConfirmed) return;

            onSelectedIdsChange?.(selectedIds.filter((currentId) => String(currentId) !== id));
            return;
        }

        const confirmation = await Swal.fire({
            icon: "question",
            title: "Autorizar geocerca",
            text: nearbySet.has(id)
                ? `Deseas autorizar "${name}" en el trayecto activo?`
                : `Deseas autorizar manualmente "${name}" en el trayecto activo?`,
            showCancelButton: true,
            confirmButtonText: "Si, autorizar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#15803d",
        });
        if (!confirmation.isConfirmed) return;

        onSelectedIdsChange?.([...selectedIds, id]);
    };

    const requestClose = async () => {
        if (!hasUnsavedChanges) {
            onClearTrayecto?.();
            toggle?.();
            return;
        }

        const confirmation = await Swal.fire({
            icon: "warning",
            title: "Cambios sin guardar",
            text: "Tienes cambios de autorizacion sin guardar. Si cierras el panel se perdera este borrador. Deseas cerrar?",
            showCancelButton: true,
            confirmButtonText: "Si, cerrar",
            cancelButtonText: "Seguir editando",
            confirmButtonColor: "#b42318",
        });

        if (!confirmation.isConfirmed) return;
        onDiscardDraft?.();
        onClearTrayecto?.();
        toggle?.();
    };

    const clearTemporarySelection = () => {
        onSelectedIdsChange?.([]);
    };

    const toggleStartPoint = (geocerca) => {
        const pointPayload = {
            id: geocerca.id,
            name: geocerca.name,
            lat: geocerca.center?.lat ?? null,
            lng: geocerca.center?.lng ?? null,
        };

        if (startPointId === geocerca.id) {
            onStartPointChange?.(null);
            if (endPointId && endPointId === geocerca.id) {
                onEndPointChange?.(null);
            }
            return;
        }

        onStartPointChange?.(pointPayload);
        if (endPointId && endPointId === geocerca.id) {
            onEndPointChange?.(null);
        }
    };

    const toggleEndPoint = (geocerca) => {
        if (!hasStartPoint || startPointId === geocerca.id) return;

        const pointPayload = {
            id: geocerca.id,
            name: geocerca.name,
            lat: geocerca.center?.lat ?? null,
            lng: geocerca.center?.lng ?? null,
        };

        if (endPointId === geocerca.id) {
            onEndPointChange?.(null);
            return;
        }

        onEndPointChange?.(pointPayload);
    };

    const clearFlow = async () => {
        if (hasUnsavedChanges) {
            const confirmation = await Swal.fire({
                icon: "warning",
                title: "Cambios sin guardar",
                text: "Tienes cambios sin guardar. Si limpias, perderas el borrador actual de autorizaciones. Deseas continuar?",
                showCancelButton: true,
                confirmButtonText: "Si, limpiar",
                cancelButtonText: "Seguir editando",
                confirmButtonColor: "#b42318",
            });

            if (!confirmation.isConfirmed) return;
            onDiscardDraft?.();
        }

        setSearch("");
        setNearbyIds([]);
        onClearTrayecto?.();
    };

    const selectedTrayectoName = trayecto ? getTrayectoName(trayecto) : "";
    const associatedCount = relatedGeocercas.filter((geocerca) => geocerca.isAssociated).length;
    const candidateCount = relatedGeocercas.filter((geocerca) => !geocerca.isAssociated).length;

    return (
        <Offcanvas
            direction="end"
            isOpen={isOpen}
            toggle={requestClose}
            backdrop={false}
            scrollable
            className="custom-offcanvas-map-center"
            container={container}
        >
            <OffcanvasHeader toggle={requestClose}>CONFIGURACIÓN DE GEOCERCAS</OffcanvasHeader>
            <OffcanvasBody>
                <div className="trayecto-geocercas">
                    <div className="trayecto-geocercas__summary">
                        <strong>Modo autorización de geocercas</strong>
                        <span>Selecciona una georuta y aplica para ver sus geocercas autorizadas y las detectadas por proximidad.</span>
                    </div>

                    <div className="trayecto-geocercas__route-box">
                        <label className="trayecto-geocercas__route-label" htmlFor="trayecto-georuta-select">
                            Georuta
                        </label>
                        <select
                            id="trayecto-georuta-select"
                            className="trayecto-geocercas__route-select"
                            value={selectedTrayectoId}
                            onChange={(event) => setSelectedTrayectoId(event.target.value)}
                            disabled={loadingGeorutas || applyingTrayecto || isUpdatingTrayecto}
                        >
                            <option value="">Selecciona una georuta</option>
                            {normalizedGeorutas.map((georuta) => (
                                <option key={georuta.id} value={georuta.id}>
                                    {georuta.name}
                                </option>
                            ))}
                        </select>
                        {georutasError && <div className="trayecto-geocercas__error">{georutasError}</div>}
                    </div>

                    <div className="geo-actions">
                        <button
                            type="button"
                            className="buttonSecondary"
                            onClick={clearFlow}
                            disabled={loadingGeorutas || applyingTrayecto || isUpdatingTrayecto}
                        >
                            Limpiar
                        </button>
                        <button
                            type="button"
                            className="buttonPrimary"
                            onClick={applyTrayectoSelection}
                            disabled={!selectedTrayectoId || loadingGeorutas || applyingTrayecto || isUpdatingTrayecto}
                        >
                            {applyingTrayecto ? "Aplicando..." : "Aplicar"}
                        </button>
                    </div>

                    {!trayecto ? (
                        <div className="trayecto-geocercas__empty">
                            Selecciona una georuta y presiona "Aplicar" para analizar el trayecto.
                        </div>
                    ) : (
                        <>
                            <div className="trayecto-geocercas__summary">
                                <strong>{selectedTrayectoName}</strong>
                                <span>{associatedCount} autorizada(s) y {candidateCount} sin autorizar detectada(s) por proximidad.</span>
                                {hasUnsavedChanges && (
                                    <span className="trayecto-geocercas__draft-indicator">
                                        Cambios sin guardar: +{draftAddedCount} / -{draftRemovedCount}
                                    </span>
                                )}
                                {(hasStartPoint || hasEndPoint) && (
                                    <span className="trayecto-geocercas__point-summary">
                                        {hasStartPoint ? `Inicio: ${getPointName(startPoint)}` : "Inicio: sin definir"}
                                        {hasEndPoint ? ` | Final: ${getPointName(endPoint)}` : " | Final: sin definir"}
                                    </span>
                                )}
                                {hasUnsavedPointChanges && (
                                    <span className="trayecto-geocercas__draft-indicator">
                                        Cambios pendientes en punto inicial/final.
                                    </span>
                                )}
                            </div>

                            <div className="trayecto-geocercas__legend">
                                <span className="trayecto-geocercas__legend-item is-associated">Autorizadas</span>
                                <span className="trayecto-geocercas__legend-item is-candidate">Sin autorizar</span>
                            </div>

                            {hasPendingPointWarning && (
                                <div className="trayecto-geocercas__warning">
                                    Se definio un punto inicial pero aun no se eligio una geocerca final. Es solo una advertencia y puedes guardar igualmente.
                                </div>
                            )}

                            <div className="trayecto-geocercas__search">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Buscar geocerca autorizada o detectada..."
                                    disabled={loading || isUpdatingTrayecto}
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch("")}
                                        aria-label="Limpiar busqueda"
                                        disabled={isUpdatingTrayecto}
                                    >
                                        x
                                    </button>
                                )}
                            </div>

                            <div className="trayecto-geocercas__list">
                                {loading ? (
                                    <div className="trayecto-geocercas__empty">Comprobando geocercas cercanas...</div>
                                ) : error ? (
                                    <div className="trayecto-geocercas__error">{error}</div>
                                ) : filteredGeocercas.length === 0 ? (
                                    <div className="trayecto-geocercas__empty">
                                        No hay geocercas relacionadas con esta georuta.
                                    </div>
                                ) : (
                                    filteredGeocercas.map((geocerca) => (
                                        <button
                                            type="button"
                                            key={geocerca.id}
                                            className={`trayecto-geocercas__item ${geocerca.isAssociated ? "is-associated" : "is-candidate"}`}
                                            onClick={() => toggleGeocerca(geocerca)}
                                            disabled={isUpdatingTrayecto}
                                            style={{
                                                backgroundColor: geocerca.colors.fill,
                                                borderColor: geocerca.colors.stroke,
                                            }}
                                        >
                                            <span
                                                className={`trayecto-geocercas__selector ${geocerca.isAssociated ? "is-associated" : "is-candidate"}`}
                                                style={{
                                                    backgroundColor: geocerca.colors.stroke,
                                                    borderColor: geocerca.colors.stroke,
                                                }}
                                            >
                                                {geocerca.isAssociated ? "OK" : "+"}
                                            </span>
                                            <span className="trayecto-geocercas__name">{geocerca.name}</span>
                                            <span
                                                className={`trayecto-geocercas__badge ${geocerca.isAssociated ? "is-associated" : "is-candidate"}`}
                                                style={{
                                                    backgroundColor: geocerca.colors.badgeBg,
                                                    color: geocerca.colors.badgeColor,
                                                }}
                                            >
                                                {geocerca.isAssociated ? "Autorizada" : "Sin autorizar"}
                                            </span>
                                            {startPointId === geocerca.id && (
                                                <span className="trayecto-geocercas__point-badge is-start">
                                                    Inicio
                                                </span>
                                            )}
                                            {endPointId === geocerca.id && (
                                                <span className="trayecto-geocercas__point-badge is-end">
                                                    Final
                                                </span>
                                            )}
                                            <span className="trayecto-geocercas__point-actions">
                                                {(!hasStartPoint || startPointId === geocerca.id) && (
                                                    <button
                                                        type="button"
                                                        className={`trayecto-geocercas__point-button ${startPointId === geocerca.id ? "is-active" : ""}`}
                                                        title={startPointId === geocerca.id ? "Quitar punto inicial" : "Marcar como punto inicial"}
                                                        aria-label={startPointId === geocerca.id ? "Quitar punto inicial" : "Marcar como punto inicial"}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            toggleStartPoint(geocerca);
                                                        }}
                                                        disabled={isUpdatingTrayecto}
                                                    >
                                                        <i className={`bx ${startPointId === geocerca.id ? "bx-minus" : "bx-map-pin"}`} />
                                                    </button>
                                                )}
                                                {hasStartPoint && startPointId !== geocerca.id && (
                                                    <button
                                                        type="button"
                                                        className={`trayecto-geocercas__point-button is-end ${endPointId === geocerca.id ? "is-active" : ""}`}
                                                        title={endPointId === geocerca.id ? "Quitar punto final" : "Marcar como punto final"}
                                                        aria-label={endPointId === geocerca.id ? "Quitar punto final" : "Marcar como punto final"}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            toggleEndPoint(geocerca);
                                                        }}
                                                        disabled={isUpdatingTrayecto}
                                                    >
                                                        <i className={`bx ${endPointId === geocerca.id ? "bx-x" : "bx-flag"}`} />
                                                    </button>
                                                )}
                                            </span>
                                        </button>
                                    ))
                                )}
                            </div>

                            <div className="geo-actions mt-3">
                                <button
                                    type="button"
                                    className="buttonSecondary"
                                    onClick={clearTemporarySelection}
                                    disabled={isUpdatingTrayecto}
                                >
                                    Quitar todas
                                </button>
                                <button
                                    type="button"
                                    className="buttonPrimary"
                                    disabled={isUpdatingTrayecto}
                                    onClick={() => onUpdateTrayecto?.()}
                                >
                                    {isUpdatingTrayecto ? "Actualizando..." : "Actualizar Trayecto"}
                                </button>
                            </div>

                            <small>
                                El panel se mantiene activo para autorizar o desautorizar desde el mapa sin perder el borrador. Verde indica autorizadas y rojo pastel indica geocercas sin autorizar detectadas por proximidad.
                            </small>
                        </>
                    )}
                </div>

                <style>{`
                    .trayecto-geocercas,
                    .trayecto-geocercas *,
                    .trayecto-geocercas *::before,
                    .trayecto-geocercas *::after { box-sizing:border-box; }
                    .trayecto-geocercas {
                        display:flex; flex-direction:column; gap:10px; width:100%;
                        max-width:100%; overflow-x:hidden;
                    }
                    .trayecto-geocercas__summary, .trayecto-geocercas__route-box {
                        display:flex; flex-direction:column; gap:6px; padding:10px;
                        border:1px solid rgba(0,0,0,.08); border-radius:10px; background:rgba(0,0,0,.02);
                        width:100%; max-width:100%;
                    }
                    .trayecto-geocercas__summary span, .trayecto-geocercas small { color:#666; }
                    .trayecto-geocercas__draft-indicator {
                        color:#0f4c81 !important; font-weight:700;
                    }
                    .trayecto-geocercas__point-summary {
                        color:#31546f !important; font-weight:600;
                    }
                    .trayecto-geocercas__warning {
                        padding:10px 12px; border-radius:10px;
                        background:#fff4ce; border:1px solid #f7c948; color:#8a5a00;
                        font-size:13px; line-height:1.4;
                    }
                    .trayecto-geocercas__route-label { font-size:13px; font-weight:700; color:#16324a; }
                    .trayecto-geocercas__route-select {
                        width:100%; padding:10px 12px; border-radius:10px;
                        border:1px solid rgba(0,0,0,.12); outline:none; background:#fff;
                    }
                    .trayecto-geocercas__legend {
                        display:flex; gap:8px; flex-wrap:wrap; width:100%; max-width:100%;
                    }
                    .trayecto-geocercas__legend-item {
                        padding:4px 10px; border-radius:999px; font-size:12px; font-weight:700;
                    }
                    .trayecto-geocercas__legend-item.is-associated {
                        background:${ASSOCIATED_COLORS.badgeBg}; color:${ASSOCIATED_COLORS.badgeColor};
                    }
                    .trayecto-geocercas__legend-item.is-candidate {
                        background:${CANDIDATE_COLORS.badgeBg}; color:${CANDIDATE_COLORS.badgeColor};
                    }
                    .trayecto-geocercas__search { position:relative; display:flex; align-items:center; }
                    .trayecto-geocercas__search input {
                        width:100%; padding:10px 42px 10px 12px;
                        border-radius:10px; border:1px solid rgba(0,0,0,.12); outline:none;
                        max-width:100%;
                    }
                    .trayecto-geocercas__search button {
                        position:absolute; right:10px; top:50%; transform:translateY(-50%);
                        width:22px; height:22px; border:0; padding:0;
                        border-radius:999px; background:rgba(148,163,184,.18);
                        color:transparent; font-size:0; line-height:1;
                        display:grid; place-items:center; cursor:pointer;
                    }
                    .trayecto-geocercas__search button::before {
                        content:"x";
                        color:#31546f; font-size:12px; font-weight:700; line-height:1;
                    }
                    .trayecto-geocercas__search button:hover { background:rgba(148,163,184,.3); }
                    .trayecto-geocercas__search button:disabled { cursor:not-allowed; opacity:.6; }
                    .trayecto-geocercas__list {
                        max-height:48vh; overflow:auto; padding:10px;
                        border:1px solid rgba(0,0,0,.08); border-radius:10px; background:#fff;
                        width:100%; max-width:100%; overflow-x:hidden;
                    }
                    .trayecto-geocercas__item {
                        display:flex; align-items:center; gap:10px; padding:8px;
                        width:100%; text-align:left; border:1px solid transparent;
                        border-radius:10px; cursor:pointer; margin-bottom:8px;
                        max-width:100%; overflow:hidden;
                    }
                    .trayecto-geocercas__item:last-child { margin-bottom:0; }
                    .trayecto-geocercas__item:disabled { cursor:not-allowed; opacity:.75; }
                    .trayecto-geocercas__selector {
                        width:28px; height:28px; flex:0 0 28px; border-radius:50%;
                        display:grid; place-items:center; border:2px solid #94a3b8;
                        color:#fff; font-size:11px; font-weight:800; line-height:1;
                    }
                    .trayecto-geocercas__name {
                        min-width:0; flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
                        color:#16324a; font-weight:600;
                    }
                    .trayecto-geocercas__badge {
                        padding:3px 8px; border-radius:999px; font-size:11px; font-weight:700;
                        max-width:100%; white-space:nowrap; flex:0 0 auto;
                    }
                    .trayecto-geocercas__point-badge {
                        padding:3px 8px; border-radius:999px; font-size:11px; font-weight:700;
                        white-space:nowrap; flex:0 0 auto;
                    }
                    .trayecto-geocercas__point-badge.is-start {
                        background:#dbeafe; color:#1d4ed8;
                    }
                    .trayecto-geocercas__point-badge.is-end {
                        background:#ede9fe; color:#6d28d9;
                    }
                    .trayecto-geocercas__point-actions {
                        display:flex; align-items:center; gap:6px; flex:0 0 auto;
                    }
                    .trayecto-geocercas__point-button {
                        width:30px; height:30px; border:none; border-radius:999px;
                        display:grid; place-items:center; background:rgba(255,255,255,.82);
                        color:#31546f; box-shadow:0 4px 10px rgba(15,76,129,.12); cursor:pointer;
                    }
                    .trayecto-geocercas__point-button i { font-size:16px; }
                    .trayecto-geocercas__point-button.is-active {
                        background:#0f4c81; color:#fff;
                    }
                    .trayecto-geocercas__point-button.is-end.is-active {
                        background:#7c3aed; color:#fff;
                    }
                    .trayecto-geocercas__point-button:disabled {
                        cursor:not-allowed; opacity:.6;
                    }
                    .trayecto-geocercas__empty { padding:12px 6px; text-align:center; color:#666; }
                    .trayecto-geocercas__error { padding:12px 6px; text-align:center; color:#b42318; }
                `}</style>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default GeocercasTrayectoOffcanvas;
